/**
 * Data migrations that are still live, in one place.
 *
 * Stored shapes change: a field moves, a map leaves the project for the
 * document it is about. The old data cannot be rewritten in one go, because a
 * page may not be synced on this machine yet and another device may still be
 * writing the old shape. So each migration is a pass the daemon runs for every
 * project it hosts, every round. It is keyed by id, so running it twice or on
 * two devices at once changes nothing, and once a project holds nothing old it
 * costs one read.
 *
 * One file per migration, named for the RFC that changed the shape. A
 * migration leaves this list when its RFC's cleanup slice deletes the old
 * fields, and its file goes with it.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import { moveProjectComments } from './0339-comments-onto-pages'

export interface Migration {
  /** The RFC that changed the shape, and what moves. */
  id: string
  /** One pass over one project. Returns a line worth logging, or null when
   *  there was nothing to do. */
  run(repo: Repo, projectId: string): Promise<string | null>
}

export const MIGRATIONS: Migration[] = [
  {
    id: '0339 comments and pairings onto their pages',
    run: async (repo, projectId) => {
      const r = await moveProjectComments(repo, projectId)
      if (!r.comments && !r.pairings && !r.left && !r.nowhere) return null
      return (
        `moved ${r.comments} comment(s) and ${r.pairings} pairing(s); ` +
        `${r.left} wait for a page or session to sync, ` +
        `${r.nowhere} have nowhere to go`
      )
    },
  },
]

/**
 * Run every live migration over one project. One failing does not stop the
 * rest: each is independent and will run again next round.
 */
export async function runMigrations(
  repo: Repo,
  projectId: string,
  log: (line: string) => void,
): Promise<void> {
  for (const m of MIGRATIONS) {
    try {
      const line = await m.run(repo, projectId)
      if (line) log(`migration ${m.id} in ${projectId.slice(0, 8)}: ${line}`)
    } catch (err) {
      log(
        `migration ${m.id} in ${projectId.slice(0, 8)} failed: ` +
          (err instanceof Error ? err.message : String(err)),
      )
    }
  }
}

export {
  type MoveResult,
  moveProjectComments,
} from './0339-comments-onto-pages'
